<template>
  <div class="invoice-manager">
    <!-- Header -->
    <header class="manager-header">
      <div class="header-content">
        <div class="header-left">
          <h1>📄 {{ t('invoiceManager.title') }}</h1>
          <p>{{ t('invoiceManager.subtitle') }}</p>
        </div>
        
        <div class="header-right">
          <button @click="showCreateModal = true" class="create-btn">
            ➕ {{ t('invoiceManager.createInvoice') }}
          </button>
          <router-link to="/saas/dashboard" class="back-btn">
            🏠 {{ t('invoiceManager.dashboard') }}
          </router-link>
        </div>
      </div>
    </header>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="filters-content">
        <div class="search-group">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="t('invoiceManager.searchPlaceholder')"
            class="search-input"
          />
        </div>
        
        <div class="filter-group">
          <select v-model="statusFilter" class="filter-select">
            <option value="">{{ t('invoiceManager.allStatus') }}</option>
            <option value="draft">{{ t('invoiceManager.draft') }}</option>
            <option value="sent">{{ t('invoiceManager.sent') }}</option>
            <option value="paid">{{ t('invoiceManager.paid') }}</option>
            <option value="overdue">{{ t('invoiceManager.overdue') }}</option>
          </select>
        </div>

        <div class="filter-group">
          <select v-model="companyFilter" class="filter-select">
            <option value="">{{ t('invoiceManager.allCompanies') }}</option>
            <option v-for="company in adminStore.companies" :key="company.id" :value="company.id">
              {{ company.name }}
            </option>
          </select>
        </div>
        
        <div class="stats-group">
          <span class="stat-item">
            {{ t('invoiceManager.total') }}: {{ filteredInvoices.length }}
          </span>
          <span class="stat-item">
            {{ t('invoiceManager.value') }}: ${{ totalInvoiceValue }}
          </span>
        </div>
      </div>
    </div>

    <!-- Invoices List -->
    <main class="invoices-main">
      <div v-if="loading" class="loading-state">
        <p>🔄 {{ t('invoiceManager.loadingInvoices') }}</p>
      </div>

      <div v-else-if="filteredInvoices.length === 0" class="empty-state">
        <div class="empty-content">
          <h3>📄 {{ t('invoiceManager.noInvoicesFound') }}</h3>
          <p v-if="searchQuery || statusFilter || companyFilter">{{ t('invoiceManager.noMatchingInvoices') }}</p>
          <p v-else>{{ t('invoiceManager.getStartedMessage') }}</p>
          <button @click="showCreateModal = true" class="create-btn">
            ➕ {{ t('invoiceManager.createFirstInvoice') }}
          </button>
        </div>
      </div>

      <div v-else class="invoices-table-container">
        <table class="invoices-table">
          <thead>
            <tr>
              <th>{{ t('invoiceManager.invoiceNumber') }}</th>
              <th>{{ t('invoiceManager.company') }}</th>
              <th>{{ t('invoiceManager.amount') }}</th>
              <th>{{ t('invoiceManager.status') }}</th>
              <th>{{ t('invoiceManager.issueDate') }}</th>
              <th>{{ t('invoiceManager.dueDate') }}</th>
              <th>{{ t('invoiceManager.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="invoice in filteredInvoices" 
              :key="invoice.id"
              class="invoice-row"
            >
              <td class="invoice-number">
                <strong>#{{ invoice.invoice_number }}</strong>
              </td>
              
              <td class="company-cell">
                <div class="company-info">
                  <div class="company-name">{{ invoice.company_name || getCompanyName(invoice.company_id) }}</div>
                  <div class="company-schema">{{ getCompanySchema(invoice.company_id) }}</div>
                </div>
              </td>
              
              <td class="amount-cell">
                <div class="amount-value">${{ formatAmount(invoice.amount) }}</div>
              </td>
              
              <td class="status-cell">
                <span :class="['status-badge', `status-${invoice.status}`]">
                  {{ formatStatus(invoice.status) }}
                </span>
              </td>
              
              <td class="date-cell">
                {{ formatDate(invoice.issue_date) }}
              </td>
              
              <td class="date-cell">
                <span :class="{ 'overdue-date': isOverdue(invoice) }">
                  {{ formatDate(invoice.due_date) }}
                </span>
              </td>
              
              <td class="actions-cell">
                <div class="action-buttons">
                  <button 
                    @click="editInvoice(invoice)" 
                    class="action-btn edit-btn"
                    :title="t('invoiceManager.editInvoice')"
                  >
                    ✏️
                  </button>
                  
                  <button 
                    v-if="invoice.status !== 'paid'"
                    @click="markAsPaid(invoice)" 
                    class="action-btn paid-btn"
                    :title="t('invoiceManager.markAsPaid')"
                  >
                    💰
                  </button>
                  
                  <button 
                    @click="deleteInvoice(invoice)" 
                    class="action-btn delete-btn"
                    :title="t('invoiceManager.deleteInvoice')"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || editingInvoice" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ editingInvoice ? `✏️ ${t('invoiceManager.editInvoice')}` : `➕ ${t('invoiceManager.createInvoice')}` }}</h2>
          <button @click="closeModal" class="modal-close">✖️</button>
        </div>

        <form @submit.prevent="saveInvoice" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label for="company_id">{{ t('invoiceManager.company') }} *</label>
              <select
                id="company_id"
                v-model="invoiceForm.company_id"
                required
                :disabled="formLoading"
              >
                <option value="">{{ t('invoiceManager.selectCompany') }}</option>
                <option 
                  v-for="company in adminStore.companies" 
                  :key="company.id" 
                  :value="company.id"
                >
                  {{ company.name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="invoice_number">{{ t('invoiceManager.invoiceNumber') }} *</label>
              <input
                id="invoice_number"
                v-model="invoiceForm.invoice_number"
                type="text"
                required
                placeholder="INV-001"
                :disabled="formLoading"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="amount">{{ t('invoiceManager.amount') }} *</label>
              <input
                id="amount"
                v-model="invoiceForm.amount"
                type="number"
                step="0.01"
                min="0"
                required
                placeholder="0.00"
                :disabled="formLoading"
              />
            </div>

            <div class="form-group">
              <label for="status">{{ t('invoiceManager.status') }} *</label>
              <select
                id="status"
                v-model="invoiceForm.status"
                required
                :disabled="formLoading"
              >
                <option value="draft">{{ t('invoiceManager.draft') }}</option>
                <option value="sent">{{ t('invoiceManager.sent') }}</option>
                <option value="paid">{{ t('invoiceManager.paid') }}</option>
                <option value="overdue">{{ t('invoiceManager.overdue') }}</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="issue_date">{{ t('invoiceManager.issueDate') }} *</label>
              <input
                id="issue_date"
                v-model="invoiceForm.issue_date"
                type="date"
                required
                :disabled="formLoading"
              />
            </div>

            <div class="form-group">
              <label for="due_date">{{ t('invoiceManager.dueDate') }} *</label>
              <input
                id="due_date"
                v-model="invoiceForm.due_date"
                type="date"
                required
                :disabled="formLoading"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="description">{{ t('invoiceManager.description') }}</label>
            <textarea
              id="description"
              v-model="invoiceForm.description"
              :placeholder="t('invoiceManager.descriptionPlaceholder')"
              rows="3"
              :disabled="formLoading"
            ></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" @click="closeModal" class="cancel-btn" :disabled="formLoading">
              {{ t('invoiceManager.cancel') }}
            </button>
            <button type="submit" class="save-btn" :disabled="formLoading">
              {{ formLoading ? `⏳ ${t('invoiceManager.saving')}...` : (editingInvoice ? t('invoiceManager.update') : t('invoiceManager.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingInvoice" class="modal-overlay" @click="deletingInvoice = null">
      <div class="modal-content danger-modal" @click.stop>
        <div class="modal-header">
          <h2>🗑️ {{ t('invoiceManager.deleteInvoice') }}</h2>
          <button @click="deletingInvoice = null" class="modal-close">✖️</button>
        </div>

        <div class="modal-body">
          <p>{{ t('invoiceManager.deleteConfirmation', { number: deletingInvoice.invoice_number }) }}</p>
          <p class="warning-text">⚠️ {{ t('invoiceManager.deleteWarning') }}</p>
        </div>

        <div class="modal-actions">
          <button @click="deletingInvoice = null" class="cancel-btn">
            {{ t('invoiceManager.cancel') }}
          </button>
          <button @click="confirmDelete" class="delete-btn" :disabled="formLoading">
            {{ formLoading ? `⏳ ${t('invoiceManager.deleting')}...` : t('invoiceManager.deleteInvoice') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAdminStore } from '../../stores/admin.js'

const router = useRouter()
const { t } = useI18n()
const adminStore = useAdminStore()

// Reactive state
const loading = ref(false)
const formLoading = ref(false)
const searchQuery = ref('')
const statusFilter = ref('')
const companyFilter = ref('')
const showCreateModal = ref(false)
const editingInvoice = ref(null)
const deletingInvoice = ref(null)

const invoiceForm = ref({
  company_id: '',
  invoice_number: '',
  amount: '',
  status: 'draft',
  issue_date: '',
  due_date: '',
  description: ''
})

// Computed
const filteredInvoices = computed(() => {
  let invoices = [...adminStore.invoices]
  
  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    invoices = invoices.filter(invoice => 
      invoice.invoice_number.toLowerCase().includes(query) ||
      (invoice.description && invoice.description.toLowerCase().includes(query)) ||
      (invoice.company_name || getCompanyName(invoice.company_id)).toLowerCase().includes(query)
    )
  }
  
  // Apply status filter
  if (statusFilter.value) {
    invoices = invoices.filter(invoice => invoice.status === statusFilter.value)
  }

  // Apply company filter
  if (companyFilter.value) {
    invoices = invoices.filter(invoice => invoice.company_id === parseInt(companyFilter.value))
  }
  
  return invoices.sort((a, b) => new Date(b.issue_date) - new Date(a.issue_date))
})

const totalInvoiceValue = computed(() => {
  const total = filteredInvoices.value.reduce((sum, invoice) => sum + parseFloat(invoice.amount || 0), 0)
  return formatAmount(total)
})

// Methods
function getCompanyName(companyId) {
  const company = adminStore.companies.find(c => c.id === companyId)
  return company ? company.name : 'Unknown Company'
}

function getCompanySchema(companyId) {
  const company = adminStore.companies.find(c => c.id === companyId)
  return company ? company.schema_name : 'unknown'
}

function formatAmount(amount) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0)
}

function formatDate(dateStr) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(dateStr))
}

function formatStatus(status) {
  const statusMap = {
    draft: '📝 Draft',
    sent: '📤 Sent',
    paid: '✅ Paid',
    overdue: '⚠️ Overdue'
  }
  return statusMap[status] || status
}

function isOverdue(invoice) {
  if (invoice.status === 'paid') return false
  return new Date(invoice.due_date) < new Date()
}

function resetForm() {
  const today = new Date().toISOString().split('T')[0]
  const dueDate = new Date()
  dueDate.setDate(dueDate.getDate() + 30)
  
  invoiceForm.value = {
    company_id: '',
    invoice_number: generateInvoiceNumber(),
    amount: '',
    status: 'draft',
    issue_date: today,
    due_date: dueDate.toISOString().split('T')[0],
    description: ''
  }
}

function generateInvoiceNumber() {
  const count = adminStore.invoices.length + 1
  return `INV-${count.toString().padStart(3, '0')}`
}

function closeModal() {
  showCreateModal.value = false
  editingInvoice.value = null
  resetForm()
}

function editInvoice(invoice) {
  editingInvoice.value = invoice
  invoiceForm.value = {
    company_id: invoice.company_id,
    invoice_number: invoice.invoice_number,
    amount: invoice.amount,
    status: invoice.status,
    issue_date: invoice.issue_date,
    due_date: invoice.due_date,
    description: invoice.description || ''
  }
}

async function saveInvoice() {
  if (formLoading.value) return
  
  formLoading.value = true
  
  try {
    const invoiceData = {
      ...invoiceForm.value,
      company_id: parseInt(invoiceForm.value.company_id),
      amount: parseFloat(invoiceForm.value.amount)
    }
    
    if (editingInvoice.value) {
      await adminStore.updateInvoice(editingInvoice.value.id, invoiceData)
    } else {
      await adminStore.createInvoice(invoiceData)
    }
    
    closeModal()
    await loadInvoices()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

async function markAsPaid(invoice) {
  if (formLoading.value) return
  
  formLoading.value = true
  
  try {
    await adminStore.updateInvoice(invoice.id, {
      ...invoice,
      status: 'paid'
    })
    
    await loadInvoices()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

function deleteInvoice(invoice) {
  deletingInvoice.value = invoice
}

async function confirmDelete() {
  if (formLoading.value || !deletingInvoice.value) return
  
  formLoading.value = true
  
  try {
    await adminStore.deleteInvoice(deletingInvoice.value.id)
    deletingInvoice.value = null
    await loadInvoices()
    
  } catch (error) {
    alert(`Error: ${error.message}`)
  } finally {
    formLoading.value = false
  }
}

async function loadInvoices() {
  loading.value = true
  try {
    await Promise.all([
      adminStore.loadInvoices(),
      adminStore.loadCompanies()
    ])
  } catch (error) {
    console.error('Failed to load invoices:', error)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Check authentication
  if (!await adminStore.checkSession()) {
    router.push('/saas/login')
    return
  }
  
  await loadInvoices()
  resetForm()
})
</script>

<style scoped>
.invoice-manager {
  min-height: 100vh;
  background: #f7fafc;
}

.manager-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left h1 {
  margin: 0;
  color: #2d3748;
  font-size: 1.75rem;
  font-weight: bold;
}

.header-left p {
  margin: 0.25rem 0 0 0;
  color: #718096;
  font-size: 0.9rem;
}

.header-right {
  display: flex;
  gap: 1rem;
}

.create-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.back-btn {
  padding: 0.75rem 1.5rem;
  background: #e2e8f0;
  color: #4a5568;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #cbd5e0;
  transform: translateY(-1px);
}

.filters-section {
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
}

.filters-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-input,
.filter-select {
  padding: 0.5rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
}

.search-input {
  width: 300px;
}

.search-input:focus,
.filter-select:focus {
  outline: none;
  border-color: #667eea;
}

.filter-select {
  background: white;
  cursor: pointer;
  min-width: 150px;
}

.stats-group {
  margin-left: auto;
  display: flex;
  gap: 1rem;
}

.stat-item {
  padding: 0.25rem 0.75rem;
  background: #edf2f7;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #4a5568;
  font-weight: 500;
}

.invoices-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-content h3 {
  color: #2d3748;
  margin-bottom: 1rem;
}

.invoices-table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.invoices-table {
  width: 100%;
  border-collapse: collapse;
}

.invoices-table th {
  background: #f7fafc;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #2d3748;
  border-bottom: 2px solid #e2e8f0;
}

.invoices-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.invoice-row:hover {
  background: #f7fafc;
}

.invoice-number {
  font-family: 'Courier New', monospace;
}

.company-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.company-name {
  font-weight: 600;
  color: #2d3748;
}

.company-schema {
  font-size: 0.8rem;
  color: #718096;
  font-family: 'Courier New', monospace;
}

.amount-value {
  font-weight: 600;
  color: #2d3748;
  font-size: 1.1rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-draft {
  background: #e2e8f0;
  color: #4a5568;
}

.status-sent {
  background: #bee3f8;
  color: #2c5282;
}

.status-paid {
  background: #c6f6d5;
  color: #22543d;
}

.status-overdue {
  background: #fed7d7;
  color: #742a2a;
}

.overdue-date {
  color: #e53e3e;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.edit-btn {
  background: #bee3f8;
  color: #2b6cb0;
}

.paid-btn {
  background: #d6f5d6;
  color: #38a169;
}

.delete-btn {
  background: #fed7d7;
  color: #e53e3e;
}

.action-btn:hover {
  transform: scale(1.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.danger-modal {
  border-top: 4px solid #e53e3e;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  color: #2d3748;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
}

.modal-form {
  padding: 1.5rem;
}

.modal-body {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #2d3748;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
}

.warning-text {
  color: #e53e3e;
  font-weight: 500;
  margin-top: 1rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.delete-btn {
  padding: 0.75rem 1.5rem;
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .invoices-main {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filters-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .stats-group {
    margin-left: 0;
    justify-content: center;
  }
  
  .invoices-table-container {
    overflow-x: auto;
  }
  
  .invoices-table {
    min-width: 800px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal-overlay {
    padding: 1rem;
  }
}
</style>